import * as React from "react";
import styled from "@emotion/styled";
import { UseFieldArrayMove, UseFieldArrayRemove } from "react-hook-form";
import { Button } from "./Button";
import { FadeIn } from "./FadeIn";
import { IconClose, IconChevron } from "./icons";
import { ScreenReaderText } from "./ScreenReaderText";

type RepeaterFieldsProps = {
  children: React.ReactNode;
  handleAppend: () => void;
  rowLabel: string;
  showAppend?: boolean;
};

function RepeaterFields({
  children,
  rowLabel,
  handleAppend,
  showAppend = true,
  ...props
}: RepeaterFieldsProps) {
  return (
    <>
      <StyledRepeaterFields {...props}>{children}</StyledRepeaterFields>

      {showAppend && (
        <AppendButton
          onClick={handleAppend}
          title={`Add ${rowLabel}`}
          size="xs"
        >
          Add {rowLabel}
        </AppendButton>
      )}
    </>
  );
}

type RepeaterFieldProps = {
  children: React.ReactNode;
  order: number;
  rowLabel: string;
  handleDelete: () => void;
  handleMoveUp?: () => void;
  handleMoveDown?: () => void;
};

function RepeaterField({
  order,
  children,
  rowLabel,
  handleDelete,
  handleMoveUp,
  handleMoveDown,
  ...props
}: RepeaterFieldProps) {
  return (
    <StyledRepeaterField {...props}>
      <Actions>
        <RepeaterLabel>
          {rowLabel} {order + 1}
        </RepeaterLabel>

        <MoveButton
          dir="up"
          onClick={handleMoveUp}
          disabled={!handleMoveUp}
          title={`Move ${rowLabel} up one`}
        >
          <ScreenReaderText>Move item up one row</ScreenReaderText>
          <IconChevron size=".75em" aria-label="hidden" />
        </MoveButton>

        <MoveButton
          dir="down"
          onClick={handleMoveDown}
          disabled={!handleMoveDown}
          title={`Move ${rowLabel} down one`}
        >
          <ScreenReaderText>Move item down one row</ScreenReaderText>
          <IconChevron size=".75em" aria-label="hidden" />
        </MoveButton>

        <DeleteButton onClick={handleDelete} title={`Delete ${rowLabel}`}>
          <ScreenReaderText>Delete row</ScreenReaderText>
          <IconClose aria-label="hidden" />
        </DeleteButton>
      </Actions>

      <Inputs>
        <RepeaterLabelMobile>
          <span>
            {rowLabel} {order + 1}
          </span>
        </RepeaterLabelMobile>

        {children}
      </Inputs>
    </StyledRepeaterField>
  );
}

/**
 * Create event handlers for each RepeaterField's ActionButtons
 *
 * @param index - Current index of fields[] element
 * @param length - useFieldArray's fields.length
 * @param move - useFieldArray's move function
 * @param remove - useFieldArray's remove function
 */
RepeaterField.createHandlers = (
  index: number,
  length: number,
  move: UseFieldArrayMove,
  remove: UseFieldArrayRemove
) => ({
  handleDelete: () => remove(index),
  handleMoveUp: index > 0 ? () => move(index, index - 1) : undefined,
  handleMoveDown: index < length - 1 ? () => move(index, index + 1) : undefined,
});

const StyledRepeaterFields = styled.ul(({ theme }) => ({
  listStyleType: "none",
  padding: 0,
  margin: 0,
  "li+li": {
    borderTop: `2px solid ${theme.colors.fg20}`,
  },
  "li:last-of-type": {
    marginBottom: "1rem",
  },
  fieldset: {
    marginBottom: 0,
  },
}));

const AppendButton = styled(Button)(({ theme }) => ({
  marginTop: 0,
  fontSize: theme.fontSizes.xs,
}));
AppendButton.defaultProps = {
  variant: "secondary",
};

const StyledRepeaterField = styled(FadeIn)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  "&:first-of-type": {
    borderTop: `2px solid ${theme.colors.fg20}`,
  },
  "&:last-of-type": {
    borderBottom: `2px solid ${theme.colors.fg20}`,
  },
  [theme.breakpoints.desktop]: {
    flexDirection: "row",
  },
  "label, legend": {
    fontSize: theme.fontSizes.xs,
  },
})).withComponent("li");

const Actions = styled.div(({ theme }) => ({
  background: theme.colors.fg10,
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  gap: "0.5rem",
  padding: "0.5rem 1rem",
  [theme.breakpoints.desktop]: {
    flexDirection: "column",
    justifyContent: "flex-start",
    padding: "1rem 0.5rem",
  },
}));

const RepeaterLabel = styled.div(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flex: 1,
  fontSize: theme.fontSizes.xs,
  fontWeight: "bold",
  [theme.breakpoints.desktop]: {
    alignItems: "flex-start",
    display: "none",
    justifyContent: "center",
    flex: "0 0 auto",
    marginBottom: 10,
  },
}));

const RepeaterLabelMobile = styled(RepeaterLabel)(({ theme }) => ({
  display: "none",
  position: "relative",
  // margin: 0,
  margin: "-.5rem 0",
  "&::before": {
    content: "''",
    position: "absolute",
    borderBottom: `1px solid ${theme.colors.fg20}`,
    backgroundColor: "rgb(0 0 0 / .05)",
    inset: "-2rem -2rem -.5rem -2rem",
    zIndex: 1,
  },
  span: {
    position: "relative",
    zIndex: 2,
  },
  [theme.breakpoints.desktop]: {
    display: "block",
  },
}));

const ActionButton = styled.button(({ theme }) => ({
  alignItems: "center",
  backgroundColor: "rgb(0 0 0 / 0)",
  borderColor: theme.colors.fg,
  borderRadius: theme.borderRadius.round,
  border: `1px solid`,
  display: "flex",
  justifyContent: "center",
  height: 25,
  margin: 0,
  padding: 0,
  width: 25,
  justifySelf: "flex-end",
  [theme.breakpoints.desktop]: {
    height: 20,
    width: 20,
  },
  "&[disabled]": {
    opacity: ".2",
  },
  svg: {
    height: "auto",
  },
  "&:hover, &:focus-visible": {
    boxShadow: `0 0 0 1px ${theme.colors.fg}`,
  },
}));
ActionButton.defaultProps = { type: "button" };

const MoveButton = styled(ActionButton, {
  shouldForwardProp: (prop) => prop !== "dir",
})<{ dir: "up" | "down" }>(({ dir, theme }) => ({
  borderColor: theme.colors.fg,
  color: theme.colors.fg,
  cursor: "pointer",
  svg: {
    marginBottom: dir === "down" ? -2 : 2,
    transform: dir === "down" ? "rotate(180deg)" : undefined,
    width: 12,
  },
}));

const DeleteButton = styled(ActionButton)(({ theme }) => ({
  borderColor: theme.colors.red,
  color: theme.colors.red,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  svg: {
    width: 10,
  },
  [theme.breakpoints.desktop]: {
    marginTop: 10,
  },
}));

const Inputs = styled.div(({ theme }) => ({
  flex: 1,
  display: "flex",
  gap: "1rem",
  flexDirection: "column",
  overflow: "hidden",
  padding: "1rem 1rem 1.5rem 1rem",
  borderLeft: `1px solid ${theme.colors.fg10}`,
  borderRight: `1px solid ${theme.colors.fg10}`,
  [theme.breakpoints.desktop]: {
    borderLeft: 0,
  },
  label: {
    lineHeight: 1.3,
  },
  "input, textarea, select": {
    padding: ".5rem",
    minHeight: "2rem",
    marginTop: 6,
  },
  select: {
    paddingRight: "3em",
    marginTop: 0,
  },
}));

export { RepeaterFields, RepeaterField };
